import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Heart, Droplets, Users, Target, Globe, ArrowRight, CheckCircle } from 'lucide-react';
import { mockData } from '../mock';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Mission = () => {
  return (
    <div className="min-h-screen py-16">
      <SEO
        title="Mission & Impact"
        description="Notre mission : transformer vos investissements publicitaires en impact social concret. Financement de puits d'eau en Afrique via l'Association Ulker's."
        path="/mission-impact"
      />
      {/* Header Section */}
      <section className="py-16 bg-gradient-to-br from-sky-50 via-white to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {mockData.mission.title}
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            {mockData.mission.subtitle}
          </p>
        </div>
      </section>

      {/* Main Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                  Une mission double et solidaire
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {mockData.mission.mainText}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="p-6 border-0 bg-gradient-to-br from-sky-50 to-sky-100">
                  <CardContent className="p-0">
                    <div className="flex items-center space-x-3 mb-4">
                      <Droplets className="h-6 w-6 text-sky-600" />
                      <h3 className="text-lg font-semibold text-gray-900">En France</h3>
                    </div>
                    <p className="text-gray-600">
                      Offrir une eau gratuite et de qualité à tous les consommateurs français.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="p-6 border-0 bg-gradient-to-br from-emerald-50 to-emerald-100">
                  <CardContent className="p-0">
                    <div className="flex items-center space-x-3 mb-4">
                      <Globe className="h-6 w-6 text-emerald-600" />
                      <h3 className="text-lg font-semibold text-gray-900">À l'international</h3>
                    </div>
                    <p className="text-gray-600">
                      Construire des puits d'eau potable dans les régions qui en ont besoin.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                {mockData.mission.images.map((image, index) => (
                  <div key={index} className="rounded-xl overflow-hidden shadow-lg">
                    <img
                      src={image}
                      alt={index === 0 ? "Puits d'eau potable construit en Afrique grâce aux campagnes publicitaires" : "Bénéficiaires de l'accès à l'eau potable financé par Eau Gratuite"}
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
              <div className="absolute -top-4 -right-4 w-72 h-72 bg-gradient-to-br from-emerald-200 to-sky-200 rounded-full blur-3xl opacity-20"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-sky-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {mockData.mission.partnership.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {mockData.mission.partnership.description}
            </p>
          </div>
          
          <Card className="p-8 border-0 shadow-xl bg-white">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div className="space-y-4">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-400 to-sky-400 rounded-xl mx-auto">
                    <Heart className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Transparence</h3>
                  <p className="text-gray-600">
                    Suivi en temps réel de l'utilisation des fonds et des projets financés.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-xl mx-auto">
                    <Target className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Impact Direct</h3>
                  <p className="text-gray-600">
                    Chaque bouteille consommée contribue directement à la construction d'un puits.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-400 to-sky-400 rounded-xl mx-auto">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Communauté</h3>
                  <p className="text-gray-600">
                    Rejoignez une communauté engagée pour l'accès universel à l'eau potable.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="text-center mt-12">
            <div className="bg-gradient-to-r from-emerald-500 to-sky-500 text-white p-8 rounded-2xl shadow-xl">
              <h3 className="text-2xl font-bold mb-4">Notre objectif</h3>
              <p className="text-xl font-semibold">
                {mockData.mission.partnership.impact}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Statistics */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Notre impact en chiffres
            </h2>
            <p className="text-xl text-gray-600">
              Des résultats concrets grâce à votre engagement
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '1M+', label: 'Bouteilles distribuées', icon: Droplets, color: 'sky' },
              { value: '50+', label: 'Puits construits', icon: Target, color: 'emerald' },
              { value: '100K+', label: 'Personnes aidées', icon: Users, color: 'sky' },
              { value: '100+', label: 'Partenaires engagés', icon: Heart, color: 'emerald' }
            ].map((stat, index) => (
              <Card key={index} className="p-6 text-center border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-0 space-y-4">
                  <div className={`flex items-center justify-center w-16 h-16 bg-gradient-to-r from-${stat.color}-400 to-${stat.color === 'sky' ? 'emerald' : 'sky'}-400 rounded-xl mx-auto`}>
                    <stat.icon className="h-8 w-8 text-white" />
                  </div>
                  <div className={`text-3xl font-bold text-${stat.color}-600`}>{stat.value}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-sky-50 to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Témoignages de nos bénéficiaires
            </h2>
          </div>
          
          <Card className="p-8 border-0 shadow-xl bg-white">
            <CardContent className="p-0 text-center">
              <div className="mb-6">
                <img
                  src={mockData.mission.testimonials[0].avatar}
                  alt="Témoignage bénéficiaire de l'accès à l'eau potable"
                  className="w-20 h-20 rounded-full mx-auto object-cover"
                />
              </div>
              <blockquote className="text-xl text-gray-700 italic mb-6">
                "{mockData.mission.testimonials[0].text}"
              </blockquote>
              <div className="text-lg font-semibold text-gray-900">
                {mockData.mission.testimonials[0].author}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-sky-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-white">
              Rejoignez notre mission
            </h2>
            <p className="text-xl text-emerald-100 leading-relaxed">
              Ensemble, créons un monde où l'eau potable est accessible à tous.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-white text-emerald-600 hover:bg-emerald-50 px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl group"
                >
                  Promouvoir ma marque
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-4 rounded-xl font-semibold transition-all duration-200"
                >
                  Discuter de mon projet
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Mission;